import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, Empty } from '../../components/ui';
import { Panel } from '../../components/charts';
import {
  CX_LEVELS,
  LEVEL_NAME,
  RESULT_CHIP,
  RESULT_LABEL,
  fmtInt,
  floorLabel,
} from './Dashboard';

/* ============================================================
   Register peralatan.

   Satu baris per tag, lima kolom tingkat. Ini bentuk yang dipakai
   orang di lapangan: mereka berdiri di depan satu mesin dan ingin
   tahu sudah sampai mana mesin itu — bukan berapa persen lantainya.

   Dikelompokkan menurut lantai lalu ruang, karena serah terima
   dilakukan per lantai dan pekerjaan uji dijadwalkan per ruang.

   Tingkat yang TIDAK punya baris uji ditulis "—", bukan "belum
   lulus". L1 direkam di modul pengadaan dan L5 hanya ada beberapa di
   seluruh proyek; membaca ketiadaan baris sebagai kegagalan akan
   mewarnai merah ratusan peralatan atas dokumen yang memang tidak
   pernah dibuat untuk mereka.
   ============================================================ */

type Equipment = {
  id: string;
  tag: string;
  type: string | null;
  floor: string | null;
  room: string | null;
  levels: { level: string; result: string }[];
};

const UNKNOWN = 'ZZZ_UNKNOWN';

/** Ruang kosong dan ruang null adalah hal yang sama. */
function roomKey(room: string | null): string {
  const t = (room ?? '').trim();
  return t === '' ? UNKNOWN : t.toUpperCase();
}

function floorKey(floor: string | null): string {
  const t = (floor ?? '').trim();
  return t === '' ? UNKNOWN : t.toUpperCase();
}

export function Register({ projectId }: { projectId: string }) {
  const nav = useNavigate();
  const [rows, setRows] = useState<Equipment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [floor, setFloor] = useState('');
  const [type, setType] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ equipment: Equipment[] }>(`/cx/register?projectId=${projectId}`);
      setRows(r.equipment);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Register peralatan tidak bisa dimuat.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!rows) return <Loading rows={5} />;

  // Pilihan saringan dibentuk dari SELURUH register, bukan dari yang
  // sedang tampil: daftar yang menyusut setiap kali disaring tidak
  // bisa dipakai untuk melebarkan saringan lagi.
  const floorOptions = [...new Set(rows.map((e) => floorKey(e.floor)))].sort();
  const typeOptions = [...new Set(rows.map((e) => e.type ?? UNKNOWN))].sort();

  const q = search.trim().toUpperCase();
  const shown = rows.filter((e) => {
    if (floor !== '' && floorKey(e.floor) !== floor) return false;
    if (type !== '' && (e.type ?? UNKNOWN) !== type) return false;
    if (q === '') return true;
    return [e.tag, e.type ?? '', e.floor ?? '', e.room ?? ''].join(' ').toUpperCase().includes(q);
  });

  // Lantai lalu ruang, keduanya menaik, dan "tidak diketahui" selalu
  // paling bawah: di tengah daftar lantai ia terbaca seperti lantai.
  const byFloor = new Map<string, Map<string, Equipment[]>>();
  for (const e of shown) {
    const fk = floorKey(e.floor);
    let rooms = byFloor.get(fk);
    if (!rooms) {
      rooms = new Map<string, Equipment[]>();
      byFloor.set(fk, rooms);
    }
    const rk = roomKey(e.room);
    const bucket = rooms.get(rk);
    if (bucket) bucket.push(e);
    else rooms.set(rk, [e]);
  }
  const floors = [...byFloor.keys()].sort();

  function open(tag: string) {
    nav(`/levels?search=${encodeURIComponent(tag)}`);
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Register peralatan</h1>
          <p>
            Satu baris per tag, dengan keadaan tiap tingkat di sampingnya. Buka satu baris untuk
            melihat butir ujinya dan mencatat hasil.
          </p>
        </div>
      </div>

      {rows.length === 0 ? (
        <Empty
          title="Belum ada peralatan terdaftar"
          body="Register peralatan datang dari programme dan dari daftar pengadaan. Sebelum ada peralatan, tidak ada yang bisa diuji."
        />
      ) : (
        <>
          <div className="filters">
            <input
              className="input"
              placeholder="Cari tag, jenis, lantai, ruang…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Cari peralatan"
            />
            <select
              className="select"
              value={floor}
              onChange={(e) => setFloor(e.target.value)}
              aria-label="Saring menurut lantai"
            >
              <option value="">Semua lantai</option>
              {floorOptions.map((f) => (
                <option key={f} value={f}>
                  {f === UNKNOWN ? 'Tidak diketahui' : f}
                </option>
              ))}
            </select>
            <select
              className="select"
              value={type}
              onChange={(e) => setType(e.target.value)}
              aria-label="Saring menurut jenis"
            >
              <option value="">Semua jenis</option>
              {typeOptions.map((t) => (
                <option key={t} value={t}>
                  {t === UNKNOWN ? 'Tidak diketahui' : t}
                </option>
              ))}
            </select>
            <span className="hint">
              Menampilkan {fmtInt(shown.length)} dari {fmtInt(rows.length)} peralatan
            </span>
          </div>

          {shown.length === 0 ? (
            <Empty
              title="Tidak ada yang cocok"
              body="Tidak ada peralatan yang lolos saringan ini."
              action={
                <button
                  className="btn btn-ghost"
                  onClick={() => {
                    setSearch('');
                    setFloor('');
                    setType('');
                  }}
                >
                  Bersihkan saringan
                </button>
              }
            />
          ) : (
            floors.map((fk) => {
              const rooms = byFloor.get(fk)!;
              const count = [...rooms.values()].reduce((s, list) => s + list.length, 0);
              return (
                <Panel
                  key={fk}
                  title={fk === UNKNOWN ? 'Tidak diketahui' : floorLabel(fk)}
                  note={`${fmtInt(count)} peralatan`}
                  wide
                >
                  <div className="tbl-wrap">
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>Tag</th>
                          <th>Jenis</th>
                          {CX_LEVELS.map((l) => (
                            <th key={l} title={LEVEL_NAME[l] ?? l}>
                              {l}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      {[...rooms.keys()]
                        .sort()
                        .map((rk) => (
                          <tbody key={rk}>
                            <tr className="no-click">
                              <td colSpan={2 + CX_LEVELS.length}>
                                <b>{rk === UNKNOWN ? 'Ruang tidak diketahui' : rk}</b>
                                <span className="hint" style={{ marginLeft: 12 }}>
                                  {fmtInt(rooms.get(rk)!.length)} peralatan
                                </span>
                              </td>
                            </tr>
                            {rooms.get(rk)!.map((e) => (
                              <tr
                                key={e.id}
                                onClick={() => open(e.tag)}
                                tabIndex={0}
                                onKeyDown={(ev) => ev.key === 'Enter' && open(e.tag)}
                              >
                                <td className="num">{e.tag}</td>
                                <td>{e.type ?? '—'}</td>
                                {CX_LEVELS.map((l) => {
                                  const at = e.levels.find((x) => x.level === l);
                                  return (
                                    <td key={l}>
                                      {at ? (
                                        <span className={`chip ${RESULT_CHIP[at.result] ?? ''}`}>
                                          {RESULT_LABEL[at.result] ?? at.result}
                                        </span>
                                      ) : (
                                        <span title="Tidak ada baris uji di tingkat ini">—</span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        ))}
                    </table>
                  </div>
                </Panel>
              );
            })
          )}
        </>
      )}
    </>
  );
}
